import { useState, useCallback } from "react";
import {
  BookMarked,
  Plus,
  Pencil,
  Trash2,
  Search,
  X,
  Save,
  Loader2,
  ChevronDown,
  Tag,
  Package,
} from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import {
  useKbArticles,
  useKbArticle,
  useCreateKbArticle,
  useUpdateKbArticle,
  useDeleteKbArticle,
  type KbArticleSummary,
} from "@/hooks/use-kb";

const CATEGORY_LABELS: Record<string, string> = {
  "product-data": "Product Data",
  "test-procedure": "Testing",
  "standard": "Standards",
  "material-science": "Materials",
  "troubleshooting": "Troubleshooting",
  "process": "Process",
  "business-rule": "Business",
  "statistical": "Statistical",
};

const CATEGORY_COLORS: Record<string, string> = {
  "product-data": "bg-blue-500/15 text-blue-400 border-blue-500/20",
  "test-procedure": "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  "standard": "bg-amber-500/15 text-amber-400 border-amber-500/20",
  "material-science": "bg-violet-500/15 text-violet-400 border-violet-500/20",
  "troubleshooting": "bg-red-500/15 text-red-400 border-red-500/20",
  "process": "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
  "business-rule": "bg-orange-500/15 text-orange-400 border-orange-500/20",
  "statistical": "bg-pink-500/15 text-pink-400 border-pink-500/20",
};

function CategoryBadge({ category }: { category: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${
        CATEGORY_COLORS[category] || "bg-muted text-muted-foreground border-border"
      }`}
    >
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

interface EditorState {
  mode: "create" | "edit";
  id?: number;
  slug: string;
  category: string;
  title: string;
  content: string;
  tags: string;
  related_products: string;
}

const EMPTY_EDITOR: EditorState = {
  mode: "create",
  slug: "",
  category: "product-data",
  title: "",
  content: "",
  tags: "",
  related_products: "",
};

export function KnowledgeBase() {
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editLoadId, setEditLoadId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const { data, isLoading } = useKbArticles(filterCategory || undefined, true);
  const { data: editArticle } = useKbArticle(editLoadId);
  const createMut = useCreateKbArticle();
  const updateMut = useUpdateKbArticle();
  const deleteMut = useDeleteKbArticle();

  // When edit article loads, populate editor
  if (editArticle && editLoadId && editor?.id !== editLoadId) {
    setEditor({
      mode: "edit",
      id: editArticle.id,
      slug: editArticle.slug,
      category: editArticle.category,
      title: editArticle.title,
      content: editArticle.content,
      tags: editArticle.tags,
      related_products: editArticle.related_products,
    });
    setEditLoadId(null);
  }

  const articles = data?.articles || [];
  const categories = data?.categories || [];

  // Client-side search filter
  const filtered = searchTerm
    ? articles.filter(
        (a) =>
          a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.tags.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.slug.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : articles;

  // Group by category
  const grouped = filtered.reduce<Record<string, KbArticleSummary[]>>(
    (acc, article) => {
      const cat = article.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(article);
      return acc;
    },
    {}
  );

  const handleSave = useCallback(() => {
    if (!editor) return;
    if (!editor.slug || !editor.title || !editor.content) return;

    if (editor.mode === "create") {
      createMut.mutate(
        {
          slug: editor.slug,
          category: editor.category,
          title: editor.title,
          content: editor.content,
          tags: editor.tags,
          related_products: editor.related_products,
        },
        { onSuccess: () => setEditor(null) }
      );
    } else if (editor.id) {
      updateMut.mutate(
        {
          id: editor.id,
          slug: editor.slug,
          category: editor.category,
          title: editor.title,
          content: editor.content,
          tags: editor.tags,
          related_products: editor.related_products,
        },
        { onSuccess: () => setEditor(null) }
      );
    }
  }, [editor, createMut, updateMut]);

  const handleDelete = useCallback(
    (id: number) => {
      deleteMut.mutate(id, {
        onSuccess: () => {
          setDeleteConfirmId(null);
          if (editor?.id === id) setEditor(null);
        },
      });
    },
    [deleteMut, editor]
  );

  const handleEdit = (article: KbArticleSummary) => {
    setEditLoadId(article.id);
  };

  const isSaving = createMut.isPending || updateMut.isPending;

  useSetPageHeader({
    title: "Knowledge Base",
    icon: BookMarked,
    actions: (
      <>
        <span className="text-xs text-muted-foreground hidden sm:block">
          {articles.length} articles
        </span>
        <button
          onClick={() => setEditor({ ...EMPTY_EDITOR })}
          className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">New Article</span>
        </button>
      </>
    ),
  });

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-1 overflow-hidden">
        {/* Article list */}
        <div
          className={`flex flex-col border-r border-border overflow-y-auto ${
            editor ? "w-1/2 lg:w-2/5" : "w-full"
          }`}
        >
          {/* Filters */}
          <div className="sticky top-0 z-10 bg-background border-b border-border px-4 py-3 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search articles..."
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto">
              <button
                onClick={() => setFilterCategory("")}
                className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  !filterCategory
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() =>
                    setFilterCategory(filterCategory === cat ? "" : cat)
                  }
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    filterCategory === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {CATEGORY_LABELS[cat] || cat}
                </button>
              ))}
            </div>
          </div>

          {/* Article list */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No articles found
            </div>
          ) : (
            <div className="divide-y divide-border">
              {Object.entries(grouped).map(([cat, arts]) => (
                <div key={cat}>
                  <div className="sticky top-[108px] z-[5] bg-muted/50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {CATEGORY_LABELS[cat] || cat} ({arts.length})
                  </div>
                  {arts.map((article) => (
                    <div
                      key={article.id}
                      className={`group flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer ${
                        editor?.id === article.id ? "bg-muted/40" : ""
                      }`}
                      onClick={() => handleEdit(article)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {article.title}
                        </p>
                        {article.preview && (
                          <p className="mt-0.5 text-xs text-muted-foreground/70 line-clamp-1">
                            {article.preview}…
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <CategoryBadge category={article.category} />
                          {article.tags && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                              <Tag className="h-2.5 w-2.5" />
                              {article.tags.split(",").slice(0, 3).join(", ")}
                            </span>
                          )}
                          {article.related_products && (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                              <Package className="h-2.5 w-2.5" />
                              {article.related_products
                                .split(",")
                                .slice(0, 2)
                                .join(", ")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(article);
                          }}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(article.id);
                          }}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Editor panel */}
        {editor && (
          <div className="flex flex-1 flex-col overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-4 py-3">
              <h2 className="text-sm font-semibold">
                {editor.mode === "create" ? "New Article" : "Edit Article"}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={
                    isSaving || !editor.slug || !editor.title || !editor.content
                  }
                  className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Save
                </button>
                <button
                  onClick={() => setEditor(null)}
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4 p-4">
              {(createMut.error || updateMut.error) && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {(createMut.error || updateMut.error)?.message}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Title
                </label>
                <input
                  value={editor.title}
                  onChange={(e) =>
                    setEditor({ ...editor, title: e.target.value })
                  }
                  placeholder="Article title"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Slug + Category row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Slug
                  </label>
                  <input
                    value={editor.slug}
                    onChange={(e) =>
                      setEditor({ ...editor, slug: e.target.value })
                    }
                    placeholder="url-friendly-slug"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Category
                  </label>
                  <div className="relative">
                    <select
                      value={editor.category}
                      onChange={(e) =>
                        setEditor({ ...editor, category: e.target.value })
                      }
                      className="w-full appearance-none rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {CATEGORY_LABELS[cat] || cat}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Content (Markdown)
                </label>
                <textarea
                  value={editor.content}
                  onChange={(e) =>
                    setEditor({ ...editor, content: e.target.value })
                  }
                  placeholder="Article content in markdown..."
                  rows={16}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Tags + Related Products */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Tags (comma-separated)
                  </label>
                  <input
                    value={editor.tags}
                    onChange={(e) =>
                      setEditor({ ...editor, tags: e.target.value })
                    }
                    placeholder="moulding,quality,materials"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Related Products (comma-separated)
                  </label>
                  <input
                    value={editor.related_products}
                    onChange={(e) =>
                      setEditor({
                        ...editor,
                        related_products: e.target.value,
                      })
                    }
                    placeholder="pa6-gf30,injection-moulding"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirmId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-lg border border-border bg-card p-6 shadow-lg">
            <h3 className="text-sm font-semibold">Delete article?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              This will permanently remove this article from the knowledge base.
              The AI will no longer be able to reference it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteMut.isPending}
                className="flex items-center gap-1.5 rounded-lg bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
              >
                {deleteMut.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
